import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import './BusinessDetail.css';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

export default function BusinessDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('about');
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    api.get(`/api/businesses/${id}`)
      .then(res => setBusiness(res.data.data))
      .catch(() => setBusiness(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!business?.images?.length) return;
    const t = setInterval(() => setCurrentImageIndex(p => (p + 1) % business.images.length), 3000);
    return () => clearInterval(t);
  }, [business]);

  const handleReviewSubmit = async () => {
    if (!userRating || !comment.trim()) return;
    if (!user) { alert('Please login to submit a review'); return; }
    setSubmittingReview(true);
    try {
      const res = await api.post(`/api/businesses/${id}/review`, {
        name: user.name, rating: userRating, comment: comment.trim()
      });
      setBusiness(res.data.data);
      setComment(''); setUserRating(0);
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const isOwner = user && business && (business.userId === user.id || business.userId?._id === user.id || business.userId?.toString() === user.id);

  const handleEnquiry = async () => {
    await api.post(`/api/businesses/${id}/enquiry`).catch(() => {});
    if (business?.phone) window.location.href = `tel:${business.phone}`;
  };

  const handleServiceChat = (serviceName) => {
    navigate(`/messages?businessId=${business._id}&product=${encodeURIComponent(serviceName)}`);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 80, fontSize: 18 }}>Loading...</div>;

  if (!business) return (
    <div className="bd-not-found">
      <h2>Business Not Found</h2>
      <button onClick={() => navigate(-1)}>Go Back</button>
    </div>
  );

  const images = (business.images || []).map(img => `${BASE}/${img}`);
  const hours = business.hours || {};
  const services = business.services || [];
  const products = business.products || [];
  const reviewsList = business.reviewsList || [];
  const paymentModes = business.paymentModes || [];

  // Check if open now
  const now = new Date();
  const todayName = DAYS[now.getDay() === 0 ? 6 : now.getDay() - 1];
  const todayHours = hours[todayName];
  const isOpen = todayHours && !todayHours.closed;

  return (
    <div className="bd-page">
      {/* Image Carousel */}
      <div className="bd-carousel">
        <button className="bd-back-btn" onClick={() => navigate(-1)}>← Back</button>
        {images.length > 0 ? (
          <>
            <div className="bd-carousel-images">
              {images.map((img, idx) => (
                <img key={idx} src={img} alt={`${business.businessName} ${idx + 1}`}
                  className={idx === currentImageIndex ? 'active' : ''} />
              ))}
            </div>
            <div className="bd-carousel-dots">
              {images.map((_, idx) => (
                <span key={idx} className={idx === currentImageIndex ? 'active' : ''}
                  onClick={() => setCurrentImageIndex(idx)} />
              ))}
            </div>
          </>
        ) : (
          <div className="bd-no-image">🏢</div>
        )}
      </div>

      <div className="bd-container">
        <div className="bd-header">
          <div className="bd-header-left">
            <h1>{business.businessName}</h1>
            {business.verified && <span className="bd-verified">✅ Verified</span>}
          </div>
        </div>

        {business.tagline && <p className="bd-tagline">{business.tagline}</p>}
        <p className="bd-desc">{business.businessType}{business.established ? ` | Since ${business.established}` : ''}</p>

        <div className="bd-rating">
          <div className="bd-stars">
            {'★'.repeat(Math.round(business.rating || 0))}{'☆'.repeat(5 - Math.round(business.rating || 0))}
          </div>
          <span>{business.reviews || 0} Reviews</span>
        </div>

        <div className="bd-meta">
          <span className={`bd-status ${isOpen ? 'open' : 'close'}`}>{isOpen ? 'Open' : 'Closed'}</span>
          {business.city && <span>📍 {business.city}{business.state ? `, ${business.state}` : ''}</span>}
        </div>

        {/* Action Buttons */}
        <div className="bd-actions-grid">
          <a href={`tel:${business.phone}`} className="bd-action-btn">
            <span className="bd-action-icon">📞</span>
            <span className="bd-action-text">Call Now</span>
          </a>
          {!isOwner && (
            <button className="bd-action-btn" onClick={handleEnquiry}>
              <span className="bd-action-icon">✉️</span>
              <span className="bd-action-text">Enquiry</span>
            </button>
          )}
          {business.website && (
            <a href={business.website} target="_blank" rel="noreferrer" className="bd-action-btn">
              <span className="bd-action-icon">🌐</span>
              <span className="bd-action-text">Website</span>
            </a>
          )}
          {business.facebook && (
            <a href={business.facebook} target="_blank" rel="noreferrer" className="bd-action-btn">
              <span className="bd-action-icon">📘</span>
              <span className="bd-action-text">Facebook</span>
            </a>
          )}
          {business.instagram && (
            <a href={business.instagram} target="_blank" rel="noreferrer" className="bd-action-btn">
              <span className="bd-action-icon">📸</span>
              <span className="bd-action-text">Instagram</span>
            </a>
          )}
          {business.twitter && (
            <a href={business.twitter} target="_blank" rel="noreferrer" className="bd-action-btn">
              <span className="bd-action-icon">🐦</span>
              <span className="bd-action-text">Twitter</span>
            </a>
          )}
          {business.linkedin && (
            <a href={business.linkedin} target="_blank" rel="noreferrer" className="bd-action-btn">
              <span className="bd-action-icon">💼</span>
              <span className="bd-action-text">LinkedIn</span>
            </a>
          )}
          {business.whatsapp && (
            <a href={`https://wa.me/${business.whatsapp}`} target="_blank" rel="noreferrer" className="bd-action-btn">
              <span className="bd-action-icon">💬</span>
              <span className="bd-action-text">WhatsApp</span>
            </a>
          )}
          {business.brochure && (
            <a href={`${BASE}/${business.brochure}`} target="_blank" rel="noreferrer" className="bd-action-btn">
              <span className="bd-action-icon">📄</span>
              <span className="bd-action-text">Brochure</span>
            </a>
          )}
          <button className="bd-action-btn" onClick={() => {
            if (navigator.share) navigator.share({ title: business.businessName, url: window.location.href });
            else { navigator.clipboard.writeText(window.location.href); alert('Link copied!'); }
          }}>
            <span className="bd-action-icon">📤</span>
            <span className="bd-action-text">Share</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="bd-tabs">
          {['about','photos','services','products','reviews'].map(tab => (
            <button key={tab} className={`bd-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bd-tab-content">

          {/* About */}
          {activeTab === 'about' && (
            <>
              <div className="bd-section">
                <h3>ℹ️ About Business</h3>
                <p>{business.description || 'No description available.'}</p>
                <div className="bd-info-grid">
                  {business.established && <div className="bd-info-item"><strong>Established</strong><span>{business.established}</span></div>}
                  {business.email && <div className="bd-info-item"><strong>Email</strong><span>{business.email}</span></div>}
                  {business.phone && <div className="bd-info-item"><strong>Phone</strong><span>{business.phone}</span></div>}
                  {business.businessType && <div className="bd-info-item"><strong>Type</strong><span>{business.businessType}</span></div>}
                </div>
              </div>

              <div className="bd-section">
                <h3>📍 Address</h3>
                <p>{business.address || [business.houseNo, business.landmark, business.city, business.district, business.state, business.pincode].filter(Boolean).join(', ') || 'N/A'}</p>
                {business.logistryEnabled && business.deliveryRange && (
                  <p className="bd-delivery">🚚 {business.deliveryRange} Km Delivery Available</p>
                )}
              </div>

              {Object.keys(hours).length > 0 && (
                <div className="bd-section">
                  <h3>🕒 Opening Hours</h3>
                  <div className="bd-hours">
                    {DAYS.map(day => (
                      <div key={day} className={`bd-hour-row ${day === todayName ? 'today' : ''}`}>
                        <span className="bd-day">{day}</span>
                        <span className="bd-time">
                          {hours[day]?.closed ? '🔴 Closed' : `🟢 ${hours[day]?.open || '09:00'} - ${hours[day]?.close || '18:00'}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {paymentModes.length > 0 && (
                <div className="bd-section">
                  <h3>💳 Payment Modes</h3>
                  <div className="bd-payments">
                    {paymentModes.map(mode => <span key={mode} className="bd-payment-chip">{mode}</span>)}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Photos */}
          {activeTab === 'photos' && (
            <div className="bd-section">
              <h3>📷 Photos</h3>
              {images.length > 0 ? (
                <div className="bd-photos-grid">
                  {images.map((img, idx) => (
                    <div key={idx} className="bd-photo-item" onClick={() => setCurrentImageIndex(idx)}>
                      <img src={img} alt={`Photo ${idx + 1}`} />
                    </div>
                  ))}
                </div>
              ) : <p style={{ color: '#94a3b8' }}>No photos uploaded yet.</p>}
            </div>
          )}

          {/* Services */}
          {activeTab === 'services' && (
            <div className="bd-section">
              <h3>⚙️ Our Services</h3>
              {services.length > 0 ? (
                <div className="bd-services-list">
                  {services.map((s, idx) => (
                    <div key={idx} className="bd-service-item">
                      <span className="bd-service-icon">✓</span>
                      <span style={{ flex: 1 }}>{s.name}</span>
                      {s.price && <span style={{ fontWeight: 700, color: '#16a34a' }}>{s.price}</span>}
                      {!isOwner && (
                        <button
                          onClick={() => handleServiceChat(s.name)}
                          style={{ marginLeft: 10, background: '#00C853', color: '#fff', border: 'none', borderRadius: 8, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >💬 Chat</button>
                      )}
                    </div>
                  ))}
                </div>
              ) : <p style={{ color: '#94a3b8' }}>No services listed yet.</p>}
            </div>
          )}

          {/* Products */}
          {activeTab === 'products' && (
            <div className="bd-section">
              <h3>🛒 Our Products</h3>
              {products.length > 0 ? (
                <div className="bd-products-grid">
                  {products.map((p, idx) => (
                    <div key={idx} className="bd-product-card">
                      {p.image
                        ? <img src={`${BASE}/${p.image}`} alt={p.name} />
                        : <div style={{ height: 160, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40 }}>🛒</div>
                      }
                      <h4>{p.name}</h4>
                      {p.price && <p className="bd-product-price">₹{p.price}{p.discount ? <span style={{ fontSize: 12, color: '#ef4444', marginLeft: 6 }}>{p.discount} off</span> : ''}</p>}
                      {p.description && <p style={{ padding: '0 12px 8px', fontSize: 12, color: '#64748b' }}>{p.description}</p>}
                      {!isOwner && (
                        <button className="bd-product-btn" onClick={() => {
                          navigate(`/messages?businessId=${business._id}&product=${encodeURIComponent(p.name)}`);
                        }}>Enquire Now</button>
                      )}
                    </div>
                  ))}
                </div>
              ) : <p style={{ color: '#94a3b8' }}>No products listed yet.</p>}
            </div>
          )}

          {/* Reviews */}
          {activeTab === 'reviews' && (
            <div className="bd-section">
              <div className="bd-review-form">
                <h4 style={{ marginBottom: 12, color: '#1e293b' }}>Write a Review</h4>
                {user && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, background: '#f0fdf4', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#00C853', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: 0 }}>{user.name}</p>
                      <p style={{ fontSize: 11, color: '#64748b', margin: 0 }}>Reviewing as yourself</p>
                    </div>
                  </div>
                )}
                <div className="bd-review-stars-input">
                  {[1,2,3,4,5].map(star => (
                    <span key={star}
                      className={`bd-star-btn ${star <= (hoverRating || userRating) ? 'active' : ''}`}
                      onClick={() => setUserRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}>★</span>
                  ))}
                  <span style={{ fontSize: 14, color: '#64748b', marginLeft: 8, alignSelf: 'center' }}>
                    {userRating ? `${userRating} Star${userRating > 1 ? 's' : ''}` : 'Select rating'}
                  </span>
                </div>
                <input className="bd-review-input" placeholder="Write your comment..."
                  value={comment} onChange={e => setComment(e.target.value)} />
                {!user && (
                  <p style={{ fontSize: 12, color: '#ef4444', marginTop: -8 }}>Please login to submit a review.</p>
                )}
                <button className="bd-review-submit" onClick={handleReviewSubmit}
                  disabled={submittingReview || !user}>
                  {submittingReview ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>

              <h3>Customer Reviews ({reviewsList.length})</h3>
              {reviewsList.length > 0 ? (
                <div className="bd-reviews-list">
                  {reviewsList.map((r, idx) => (
                    <div key={idx} className="bd-review-item">
                      <div className="bd-review-header">
                        <div className="bd-review-avatar">{r.name?.charAt(0) || 'U'}</div>
                        <div className="bd-review-info">
                          <h4>{r.name}</h4>
                          <div className="bd-review-stars">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</div>
                        </div>
                        <span className="bd-review-date">{r.date}</span>
                      </div>
                      <p className="bd-review-comment">{r.comment}</p>
                    </div>
                  ))}
                </div>
              ) : <p style={{ color: '#94a3b8' }}>No reviews yet. Be the first to review!</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
